import React, { useState, useEffect } from 'react';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [products, setProducts] = useState([]);
  const [showAddOrderForm, setShowAddOrderForm] = useState(false);
  const [newOrder, setNewOrder] = useState({
    customer: '',
    items: [],
    status: 'pending'
  });
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  // Load products from localStorage
  useEffect(() => {
    const savedProducts = localStorage.getItem('products');
    if (savedProducts) {
      setProducts(JSON.parse(savedProducts));
    }
  }, []);

  // Load orders from localStorage or initialize with sample data
  useEffect(() => {
    const savedOrders = localStorage.getItem('orders');
    if (savedOrders) {
      setOrders(JSON.parse(savedOrders));
    } else {
      // Simulate fetching orders from an API
      // In a real app: fetch('/api/orders').then(res => res.json()).then(data => setOrders(data));
      const sampleOrders = [
        {
          id: 101,
          date: new Date().toISOString().split('T')[0],
          customer: 'Alice Johnson',
          items: [
            { productName: 'Product A', quantity: 2, price: 10 },
            { productName: 'Product C', quantity: 1, price: 30 }
          ],
          total: 2 * 10 + 1 * 30,
          status: 'completed'
        },
        {
          id: 102,
          date: new Date().toISOString().split('T')[0],
          customer: 'Bob Smith',
          items: [
            { productName: 'Product B', quantity: 3, price: 20 }
          ],
          total: 3 * 20,
          status: 'pending'
        }
      ];
      setOrders(sampleOrders);
      localStorage.setItem('orders', JSON.stringify(sampleOrders));
    }
  }, []);

  // Save orders to localStorage whenever orders change
  useEffect(() => {
    if (orders.length > 0) {
      localStorage.setItem('orders', JSON.stringify(orders));
    }
  }, [orders]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { text: 'Completed', color: 'bg-green-100 text-green-800', icon: '✓' },
      pending: { text: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      cancelled: { text: 'Cancelled', color: 'bg-red-100 text-red-800', icon: '✕' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  // Add item to new order
  const handleAddItemToOrder = () => {
    if (!selectedProduct || selectedQuantity <= 0) return;
    
    const product = products.find(p => p.id === parseInt(selectedProduct));
    if (!product) return;

    // Check if product already exists in order items
    const existingItemIndex = newOrder.items.findIndex(item => item.productId === product.id);
    
    if (existingItemIndex >= 0) {
      // Update quantity if product already exists
      const updatedItems = [...newOrder.items];
      updatedItems[existingItemIndex].quantity += parseInt(selectedQuantity);
      setNewOrder({ ...newOrder, items: updatedItems });
    } else {
      // Add new item
      setNewOrder({
        ...newOrder,
        items: [
          ...newOrder.items,
          {
            productId: product.id,
            productName: product.name,
            quantity: parseInt(selectedQuantity),
            price: product.price || 0
          }
        ]
      });
    }
    
    // Reset selection
    setSelectedProduct('');
    setSelectedQuantity(1);
  };

  // Remove item from new order
  const handleRemoveItemFromOrder = (index) => {
    const updatedItems = newOrder.items.filter((_, i) => i !== index);
    setNewOrder({ ...newOrder, items: updatedItems });
  };

  // Calculate total for new order
  const calculateNewOrderTotal = () => {
    return newOrder.items.reduce((sum, item) => sum + (item.quantity * (item.price || 0)), 0);
  };

  // Handle creating new order
  const handleCreateOrder = (e) => {
    e.preventDefault();
    if (!newOrder.customer || newOrder.items.length === 0) return;

    const newOrderData = {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      customer: newOrder.customer,
      items: newOrder.items.map(item => ({
        productName: item.productName,
        quantity: item.quantity,
        price: item.price
      })),
      total: calculateNewOrderTotal(),
      status: newOrder.status
    };

    // Update stock quantities
    newOrder.items.forEach(orderItem => {
      const product = products.find(p => p.id === orderItem.productId);
      if (product) {
        product.quantity = Math.max(0, product.quantity - orderItem.quantity);
      }
    });
    
    // Save updated products
    localStorage.setItem('products', JSON.stringify(products));
    setProducts([...products]);

    // Add new order
    const updatedOrders = [newOrderData, ...orders];
    setOrders(updatedOrders);
    
    // Reset form
    setNewOrder({ customer: '', items: [], status: 'pending' });
    setShowAddOrderForm(false);
    setSelectedProduct('');
    setSelectedQuantity(1);
  };

  // Filter orders
  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(order => order.status === filterStatus);

  // Calculate statistics
  const totalOrders = orders.length;
  const totalRevenue = orders.reduce((sum, order) => sum + order.total, 0);
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const completedOrders = orders.filter(o => o.status === 'completed').length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Orders</h1>
        <p className="text-slate-600">View and manage all customer orders</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 mb-1">Total Orders</p>
              <p className="text-3xl font-bold text-slate-800">{totalOrders}</p>
            </div>
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 mb-1">Total Revenue</p>
              <p className="text-3xl font-bold text-green-600">₹{totalRevenue.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 mb-1">Completed</p>
              <p className="text-3xl font-bold text-green-600">{completedOrders}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 mb-1">Pending</p>
              <p className="text-3xl font-bold text-yellow-600">{pendingOrders}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'completed', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors capitalize ${
                  filterStatus === status
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md shadow-emerald-500/30'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {status === 'all' ? 'All Orders' : status}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowAddOrderForm(!showAddOrderForm)}
            className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 shadow-sm hover:shadow-md shadow-emerald-500/30 hover:shadow-emerald-500/40 transition-all duration-200 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {showAddOrderForm ? 'Cancel' : 'Create New Order'}
          </button>
        </div>
      </div>

      {/* Add Order Form */}
      {showAddOrderForm && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 animate-fade-in">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            Create New Order
          </h2>
          
          <form onSubmit={handleCreateOrder} className="space-y-6">
            {/* Customer Name */}
            <div>
              <label htmlFor="customer-name" className="block text-sm font-medium text-slate-700 mb-2">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input
                id="customer-name"
                type="text"
                value={newOrder.customer}
                onChange={(e) => setNewOrder({ ...newOrder, customer: e.target.value })}
                placeholder="Enter customer name"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors"
                required
              />
            </div>

            {/* Add Products Section */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Add Products
              </label>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <select
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors"
                  >
                      <option value="">Select a product...</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} - Stock: {product.quantity} - ₹{(product.price || 0).toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-1">
                  <input
                    type="number"
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(e.target.value)}
                    min="1"
                    placeholder="Quantity"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors"
                  />
                </div>
                <div className="md:col-span-1">
                  <button
                    type="button"
                    onClick={handleAddItemToOrder}
                    disabled={!selectedProduct || selectedQuantity <= 0}
                    className="w-full px-4 py-2.5 bg-slate-600 text-white font-medium rounded-lg hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Add Product
                  </button>
                </div>
              </div>
            </div>

            {/* Order Items List */}
            {newOrder.items.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Order Items</h3>
                <div className="space-y-2">
                  {newOrder.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded flex items-center justify-center">
                          <span className="text-xs font-semibold text-emerald-600">{item.quantity}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-slate-800">{item.productName}</span>
                            <span className="text-xs text-slate-500 ml-2">× {item.quantity} @ ₹{(item.price || 0).toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="text-sm font-semibold text-slate-800">
                            ₹{((item.quantity * (item.price || 0)).toFixed(2))}
                          </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveItemFromOrder(index)}
                          className="w-8 h-8 flex items-center justify-center bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                  <span className="text-lg font-semibold text-slate-800">Total:</span>
                  <span className="text-2xl font-bold text-emerald-600">
                    ₹{calculateNewOrderTotal().toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex items-center justify-end space-x-4 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => {
                  setShowAddOrderForm(false);
                  setNewOrder({ customer: '', items: [], status: 'pending' });
                  setSelectedProduct('');
                  setSelectedQuantity(1);
                }}
                className="px-6 py-2.5 bg-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!newOrder.customer || newOrder.items.length === 0}
                className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 shadow-sm hover:shadow-md shadow-emerald-500/30 hover:shadow-emerald-500/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Create Order
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.map(order => {
          const statusBadge = getStatusBadge(order.status);
          return (
            <div 
              key={order.id} 
              className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-all duration-200 overflow-hidden"
            >
              <div className="p-6">
                {/* Order Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div className="flex items-center space-x-4 mb-4 md:mb-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center text-white font-bold shadow-sm shadow-emerald-500/30">
                      #{order.id}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">Order #{order.id}</h3>
                      <div className="flex items-center mt-1 text-sm text-slate-500">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(order.date)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${statusBadge.color}`}>
                      {statusBadge.icon} {statusBadge.text}
                    </span>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Total</p>
                      <p className="text-2xl font-bold text-slate-800">₹{order.total.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="mb-4 pb-4 border-b border-slate-200">
                  <div className="flex items-center text-slate-600">
                    <svg className="w-5 h-5 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="font-medium">{order.customer}</span>
                  </div>
                </div>

                {/* Order Items */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    Order Items ({order.items.length})
                  </h4>
                  <div className="space-y-2">
                    {order.items.map((item, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-emerald-100 rounded flex items-center justify-center">
                            <span className="text-xs font-semibold text-emerald-600">{item.quantity}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-slate-800">{item.productName}</span>
                            {item.price && (
                              <span className="text-xs text-slate-500 ml-2">@ ₹{item.price.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-sm text-slate-600">× {item.quantity}</span>
                          {item.price && (
                            <span className="text-xs text-slate-500 block mt-1">= ₹{(item.quantity * item.price).toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {filteredOrders.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-slate-500 text-lg font-medium">No orders found</p>
            <p className="text-slate-400 text-sm mt-1">
              {filterStatus === 'all' 
                ? 'No orders have been placed yet' 
                : `No ${filterStatus} orders at this time`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
